import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Expandable } from '@AMIEWEB/Common';
import {
  AdditionInformationSection,
  EarlyArrivalSection,
  HolidaysSection,
  HousingSection,
  MealsSection,
  OrientationSection,
  OvertimeSection,
  PayBonusSection,
  PrebookingSection,
  QMRequirementsSection,
  TravelSection,
} from './BookingDetailSections';
import { GenericTabs, Orientations } from '@AMIEWEB/Common/Tabs/GenericTabs';
import { flattenPlacementDetailReport } from './BookingDetailReportUtils';
import { Button, Typography } from '@mui/material';
import moment from 'moment';
import { selectR448ReportDetails } from 'store/redux-store/report-details/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { reportsViewActions } from 'store/redux-store/report-details/slice';
import { selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';

export const BookingDetailReport = () => {
  const { t } = useTranslation();
  const [expand, setExpand] = useState<boolean>(true);
  const r448Data = useSelector(selectR448ReportDetails);
  const placementHeaderData = useSelector(selectPlacementHeaderData)
  const dispatch = useDispatch();

  React.useEffect(() => {
    if(placementHeaderData?.orderId > 0){
      dispatch(reportsViewActions.getR448ReportDetails({ orderId: placementHeaderData.orderId, startDate: moment().format('YYYY-MM-DD') }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementHeaderData?.orderId]);

  const currentDate = useMemo(() => {
    return `${moment().format('MMMM D, YYYY')}`;
  }, []);

  const placementDetailReport = useMemo(() => {
    return flattenPlacementDetailReport(r448Data?.response);
  }, [r448Data?.response]);

  const tabs = [
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.payBonusInformation')}`,
      component: (
        <>
          <div style={{ alignContent: 'center', position: 'sticky', top: 0 }}>
            <Typography variant={'h6'}>
              {t('reportPages.r448.bookingfor')} {placementDetailReport?.unitName}
            </Typography>
            <Typography variant={'body1'} style={{ paddingBottom: 20 }}>
              {t('reportPages.r448.asof')} {currentDate}
            </Typography>
            <Button
              disableRipple
              sx={{ float: 'right' }}
              onClick={() => {
                setExpand(!expand);
              }}
            >
              {expand ? 'COLLAPSE All' : 'EXPAND All'}
            </Button>
          </div>
          <Expandable
            variant="report"
            expanded={expand}
            titleComponent={t('reportPages.r448.detailReportTab.sideNav.payBonusInformation')}
          >
            <PayBonusSection details={placementDetailReport?.payBonusSection} />
          </Expandable>
        </>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.mealsAndIncidentals')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.mealsAndIncidentals')}
        >
          <MealsSection details={placementDetailReport?.mealsAndIncidentalsSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.housing')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.housing')}
        >
          <HousingSection details={placementDetailReport?.housingSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.travel')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.travelInformation')}
        >
          <TravelSection details={placementDetailReport?.travelSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.overtime')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.overtime')}
        >
          <OvertimeSection details={placementDetailReport?.overTimeSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.holidays')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.holidays')}
        >
          <HolidaysSection details={placementDetailReport?.holidaySection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.qmRequirements')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.qmRequirements')}
        >
          <QMRequirementsSection details={placementDetailReport?.qmRequirementSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.orientation')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.orientation')}
        >
          <OrientationSection details={placementDetailReport?.orientationSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.earlyArrival')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.earlyArrival')}
        >
          <EarlyArrivalSection details={placementDetailReport?.earlyArrivalSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.additionalInformation')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.additionalInformation')}
        >
          <AdditionInformationSection details={placementDetailReport?.additionalInformationSection} />
        </Expandable>
      ),
    },
    {
      label: `${t('reportPages.r448.detailReportTab.sideNav.prebookingInstructions')}`,
      component: (
        <Expandable
          variant="report"
          expanded={expand}
          titleComponent={t('reportPages.r448.detailReportTab.sideNav.prebookingInstructions')}
        >
          <PrebookingSection details={placementDetailReport?.prebookingSection} />
        </Expandable>
      ),
    },
  ];

  return (
    <>
      <GenericTabs tabs={tabs} orientation={Orientations.verticalList} />
    </>
  );
};
